import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_PUBLIC_CLIENT_ACTIVITY } from '@bloomays-lib/adapter.api-bloomer';
import { IActivity, IMission } from '@bloomays-lib/types.shared';
import { FileViewer, LoaderGlobal, Modal } from '@bloomays-lib/ui.shared';
import CRAValidation from '../organisms/CRAValidation';
import { activityToActivitySummary } from '../../helpers/CRA';
import { parsePublicCraSigningDocumentId } from '../../helpers/craDashboard';

export type CraPreviewModalProps = {
  preview: { url: string; title: string } | undefined;
  onClose: () => void;
  accessToken?: string;
};

/**
 * CRA preview for the Bloomie dashboard.
 * Public CRA URLs (`/public/cra/...`) render `CRAValidation` inline so PDF download
 * matches the full-page `/public/cra/:id/cra.pdf` route (html2canvas in the parent
 * document). Secured file URLs keep the legacy `FileViewer` iframe.
 */
const CraPreviewModal = ({ preview, onClose, accessToken }: CraPreviewModalProps) => {
  const signingDocumentId = preview ? parsePublicCraSigningDocumentId(preview.url) : null;
  const isPublicCraPage = !!signingDocumentId;

  const [loadPublicActivity, { data, loading, called }] = useLazyQuery<
    { getPublicClientActivity: IActivity },
    { signingDocumentID: string }
  >(GET_PUBLIC_CLIENT_ACTIVITY, {
    context: { clientName: 'api.bloomers', isPublic: true },
  });

  useEffect(() => {
    if (signingDocumentId) {
      void loadPublicActivity({ variables: { signingDocumentID: signingDocumentId } });
    }
  }, [signingDocumentId, loadPublicActivity]);

  const activitySummary =
    data?.getPublicClientActivity && data.getPublicClientActivity.mission
      ? activityToActivitySummary(data.getPublicClientActivity, data.getPublicClientActivity.mission as IMission)
      : undefined;

  return (
    <Modal
      open={!!preview}
      onClose={onClose}
      additionnalCSS={
        isPublicCraPage
          ? {
              width: 'min(1000px, 95vw)',
              padding: '16px',
              maxHeight: '90vh',
              overflow: 'auto',
              border: 'none',
              borderRadius: '20px',
            }
          : {
              width: '822px',
              padding: '32px',
              height: '90%',
              border: 'none',
              borderRadius: '20px',
            }
      }
    >
      {!preview ? null : isPublicCraPage ? (
        loading || !called ? (
          <LoaderGlobal waitingText="Chargement du CRA…" />
        ) : activitySummary ? (
          <CRAValidation activitySummary={activitySummary} readOnly />
        ) : (
          <div>Impossible de charger ce CRA.</div>
        )
      ) : (
        <FileViewer width="100%" height="100%" url={preview.url} accessToken={accessToken} />
      )}
    </Modal>
  );
};

export default CraPreviewModal;
export { CraPreviewModal };
